// Per-type secondary text ("subtitle") for content-search result rows.
//
// Many records share a title (e.g. several people called "David", or a
// recurring event repeated across dates). A muted second line disambiguates
// them. Config is keyed by base type (`definition.definesType || type`).
//
// Only the types that need it today are registered — everything else,
// including `profile`, stays title-only. Each entry declares:
//   - `fields`: extra fields to add to the content list `select` so the
//     subtitle can be rendered.
//   - `subtitle(item, { sdk })`: the single best disambiguating string, or
//     a falsy value when none applies.
const REGISTRY = {
  persona: {
    fields: ["username", "email", "collectionEmailAddress", "userEmailAddress"],
    subtitle(item) {
      return (
        item.username ||
        item.email ||
        item.collectionEmailAddress ||
        item.userEmailAddress ||
        null
      );
    },
  },
  event: {
    fields: ["startDate", "endDate", "allDay", "timezone"],
    subtitle(item, { sdk } = {}) {
      const readable = sdk?.date?.readableEventDate;
      return (readable && readable(item)) || null;
    },
  },
};

// The base type used to look up subtitle config: a custom definition's
// `definesType` (e.g. a login definition that extends `persona`), falling
// back to the raw `type`.
export function subtitleBaseType(definition, type) {
  return definition?.definesType || type || null;
}

export function subtitleConfigFor(definition, type) {
  const base = subtitleBaseType(definition, type);
  return REGISTRY[base] || null;
}

// Extra fields to merge into the content list `select` for this type.
export function subtitleSelectFields(definition, type) {
  const config = subtitleConfigFor(definition, type);
  return config ? config.fields.slice() : [];
}

// Resolve the muted subtitle for an item, or null when none applies. Any
// error in a resolver is swallowed so a bad record never breaks the dropdown.
export function resolveSubtitle(item, definition, type, context) {
  if (!item) {
    return null;
  }
  const config = subtitleConfigFor(definition, type);
  if (!config) {
    return null;
  }
  try {
    return config.subtitle(item, context || {}) || null;
  } catch (err) {
    return null;
  }
}
